import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Bouton, LienBouton } from "@/composants/ui/bouton";
import { Carte } from "@/composants/ui/carte";
import { PiedDeListe } from "@/composants/ui/pied-de-liste";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { useListePaginee } from "@/lib/chargement";
import { dateCourte, euros, statutCommande } from "@/lib/libelles";
import { api, type Commande } from "@/lib/api";
import { useSession } from "@/session";

/**
 * Les dossiers — l'écran d'entrée du LOT 1.
 *
 * Une seule mise en page qui se replie, et non deux écrans : sous 768 px chaque
 * dossier occupe deux lignes empilées, au-delà il tient sur une seule. Les
 * en-têtes de colonne n'apparaissent que quand il y a des colonnes.
 */

const FILTRES = [
  { valeur: "", libelle: "Tous" },
  { valeur: "SIGNEE", libelle: "Signés" },
  { valeur: "EN_COURS", libelle: "En cours" },
  { valeur: "CLOTUREE", libelle: "Clôturés" },
  { valeur: "ANNULEE", libelle: "Annulés" },
];

export function PageAccueil() {
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const peutCreer = identite?.permissions.includes("GERER_COMMANDE_CLIENT") ?? false;
  const voitLesMontants = identite?.permissions.includes("CONSULTER_MONTANTS") ?? false;

  const [statut, setStatut] = useState("");
  const liste = useListePaginee<Commande>(
    (decalage, signal) => api.commandes({ statut: statut || undefined, decalage }, signal),
    [statut],
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-encre">Bonjour {identite?.prenom ?? ""}</h1>
          <p className="mt-1 text-sm text-encre-douce">
            Les Commandes / Projets de votre périmètre, du plus récent au plus ancien.
          </p>
        </div>
        {peutCreer ? (
          <LienBouton to="/commandes/nouvelle" variante="principal" className="shrink-0">
            <Plus className="h-4 w-4" aria-hidden />
            Nouvelle Commande / Projet
          </LienBouton>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par statut">
        {FILTRES.map((filtre) => (
          <Bouton
            key={filtre.valeur || "tous"}
            taille="compact"
            variante={statut === filtre.valeur ? "sombre" : "secondaire"}
            aria-pressed={statut === filtre.valeur}
            onClick={() => setStatut(filtre.valeur)}
          >
            {filtre.libelle}
          </Bouton>
        ))}
      </div>

      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des Commandes / Projets…" /> : null}

      {!liste.enCours && liste.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucune Commande / Projet"
            detail={
              statut
                ? "Aucune Commande / Projet ne porte ce statut dans votre périmètre."
                : "Les Commandes / Projets que vous créerez apparaîtront ici."
            }
          />
        </Carte>
      ) : null}

      {liste.elements.length > 0 ? (
        <Carte>
          <ul className="divide-y divide-filet">
            {liste.elements.map((commande) => (
              <li key={commande.id}>
                <Link
                  to={`/commandes/${commande.id}`}
                  className="flex flex-col gap-1.5 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="font-mono text-sm text-encre sm:w-28 sm:shrink-0">
                    {commande.numero_commande_client}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-encre">
                    {commande.client.designation}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-24 sm:shrink-0">
                    {dateCourte(commande.date_commande)}
                  </span>
                  {voitLesMontants ? (
                    <span className="text-sm text-encre-douce sm:w-28 sm:shrink-0 sm:text-right sm:tabular-nums">
                      {euros(commande.montant_ht)}
                    </span>
                  ) : null}
                  <span className="text-sm text-encre-douce sm:w-24 sm:shrink-0">
                    {commande.nombre_lots} lot{commande.nombre_lots > 1 ? "s" : ""}
                  </span>
                  <span className="sm:w-44 sm:shrink-0">
                    <Etiquette
                      ton={
                        commande.statut === "ANNULEE"
                          ? "alerte"
                          : commande.statut === "CLOTUREE"
                            ? "succes"
                            : commande.statut === "EN_COURS"
                              ? "actif"
                              : "neutre"
                      }
                    >
                      {statutCommande(commande.statut)}
                    </Etiquette>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <PiedDeListe
            nom="commande"
            affiches={liste.elements.length}
            total={liste.total}
            reste={liste.reste}
            enCours={liste.chargeLaSuite}
            onCharger={liste.chargerLaSuite}
          />
        </Carte>
      ) : null}
    </div>
  );
}
